import type { TenantKey } from "@/config/tenants";

import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import React, { Component, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@heroui/react";
import { UserInput } from "@webuild-ai/admin/models/components";
import { useAuth, useUser, verifyUserPermissions } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { VERSION_INFO } from "@/types/version";
import { logger } from "@/telemetry";
import {
  isFolder,
  isKnownTenantDomain,
  tenantFolders,
  tenantKeyForEmailDomain,
} from "@/config/tenants";
import { dashboardUrlSegments } from "@/config/dashboards";
import { useTenantKey } from "@/providers/TenantProvider";

import { useWebuildAPIClient } from "./providers/WeBuildClientProvider";
import { useEmbeddedAuthConfig } from "./providers/EmbeddedAuthProvider";
import LoginPrompt from "./components/LoginPrompt/LoginPrompt";
import IndexPage from "./pages/index";
import { WorkflowPage } from "./pages/workflows/WorkflowPage";
import LoadingPage, { LoadingContent } from "./pages/Loading";
import DefaultLayout from "./layouts/default";
import { IntegrationCallback } from "./pages/settings/IntegrationCallback";
import { useResponsive } from "./hooks/use-responsive";
import { useAppLogout } from "./hooks/useAppLogout";
import { useGlobalStore } from "./stores/GlobalStore";
import { TamperDetection } from "./components/Security/TamperDetection";
import { ProtectedRoute } from "./components/Security/ProtectedRoute";
import { AccessDeniedPage } from "./pages/AccessDeniedPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Lazy-loaded pages — split into separate chunks for faster initial load
const WorkflowDatasetsPage = React.lazy(() =>
  import("./pages/workflows/WorkflowDatasetsPage").then((m) => ({
    default: m.WorkflowDatasetsPage,
  })),
);
const RunPage = React.lazy(() =>
  import("./pages/workflows/RunPage").then((m) => ({ default: m.RunPage })),
);
const WorkflowPlaygroundPage = React.lazy(() =>
  import("./pages/workflows/WorkflowPlaygroundPage").then((m) => ({
    default: m.WorkflowPlaygroundPage,
  })),
);
const WorkflowMetricsPage = React.lazy(() =>
  import("./pages/workflows/WorkflowMetricsPage").then((m) => ({
    default: m.WorkflowMetricsPage,
  })),
);
const DataHubIndexPage = React.lazy(() => import("./pages/datahub/DataHubIndexPage"));
const DataHubFilesPage = React.lazy(() =>
  import("./pages/datahub/DataHubFilesPage").then((m) => ({ default: m.DataHubFilesPage })),
);
const DataHubMCPServerPage = React.lazy(() =>
  import("./pages/datahub/DataHubMCPServerPage").then((m) => ({
    default: m.DataHubMCPServerPage,
  })),
);
const DataHubGoogleDrive = React.lazy(() => import("./pages/datahub/DataHubGoogleDrive"));
const DataHubConfluence = React.lazy(() => import("./pages/datahub/DataHubConfluence"));
const SettingsPage = React.lazy(() =>
  import("./pages/settings").then((m) => ({ default: m.SettingsPage })),
);
const LibraryPage = React.lazy(() =>
  import("./pages/library").then((m) => ({ default: m.LibraryPage })),
);
const MetricsPage = React.lazy(() =>
  import("./pages/MetricsPage").then((m) => ({ default: m.MetricsPage })),
);
const ModelsPage = React.lazy(() =>
  import("./pages/ModelsPage").then((m) => ({ default: m.ModelsPage })),
);
const DashboardPage = React.lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const ComplianceDashboardPage = React.lazy(() =>
  import("./pages/compliance").then((m) => ({ default: m.ComplianceDashboardPage })),
);
const WorkflowBuilderPage = React.lazy(() =>
  import("./pages/workflow-builder/WorkflowBuilderPage").then((m) => ({
    default: m.WorkflowBuilderPage,
  })),
);
const AutomationsPage = React.lazy(() =>
  import("./pages/automations").then((m) => ({ default: m.AutomationsPage })),
);

/**
 * The Agent Studio (WorkflowEditPage) at /workflows/:workflowId/edit was
 * removed in favour of the Workflow Builder. The route is kept as a redirect
 * so existing bookmarks and deep links still resolve; the legacy "new"
 * pseudo-id maps to the Builder's blank new-workflow state.
 */
const WorkflowEditRedirect = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const target =
    workflowId && workflowId !== "new" ? `/workflow-builder/${workflowId}` : "/workflow-builder";

  return <Navigate replace to={target} />;
};

/**
 * Error boundary for lazy-loaded chunk failures (network timeouts, CDN errors).
 * Catches dynamic import errors and offers a reload button.
 */
class ChunkErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    // Detect chunk load failures (Vite/webpack dynamic import errors)
    if (
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Loading CSS chunk")
    ) {
      return { hasError: true };
    }
    throw error; // Re-throw non-chunk errors for the global boundary
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <p className="text-default-600">
            A page failed to load. This is usually a network issue.
          </p>
          <button
            className="px-4 py-2 rounded bg-brand-primary text-white hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const PrivateRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const logout = useAppLogout();
  const { isEmbedded, status: embeddedStatus } = useEmbeddedAuthConfig();
  const { adminClient } = useWebuildAPIClient();
  const { tenantKey, setTenantKey } = useTenantKey();
  const { setUserProfile, userProfile } = useUser();
  const previousUserProfileRef = useRef(userProfile);
  const { sidebar } = useGlobalStore();
  const { setIsSidebarCompact } = sidebar;
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  // Load permissions directly here where we have both user and adminClient
  const [permissionsLoaded, setPermissionsLoaded] = React.useState(false);
  const permissionsLoadedRef = React.useRef(false);

  // Load permissions ONLY AFTER userProfile is created
  React.useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const loadPermissions = async () => {
      // Wait for userProfile to be created first
      if (
        !user?.id ||
        !adminClient ||
        !isAuthenticated ||
        !userProfile ||
        permissionsLoadedRef.current
      ) {
        return;
      }

      try {
        const response = await verifyUserPermissions(adminClient, user.id);

        // Check if component is still mounted and request wasn't aborted
        if (abortController.signal.aborted || !isMounted) {
          return;
        }

        if (response && response.permissions) {
          const perms = {
            workflows: response.permissions.workflows || false,
            knowledge_hub: response.permissions.knowledge_hub || false,
            prompt_library: response.permissions.prompt_library || false,
            monitoring: response.permissions.monitoring || false,
            settings: response.permissions.settings || false,
          };

          permissionsLoadedRef.current = true;
          setPermissionsLoaded(true);

          // Store permissions with timestamp for integrity checking
          sessionStorage.setItem(
            "_perm_check",
            JSON.stringify({
              perms,
              timestamp: Date.now(),
              userId: user.id,
            }),
          );

          // Update the auth provider's permissions
          window.dispatchEvent(new CustomEvent("app:permissions-loaded", { detail: perms }));
        }
      } catch {
        // Only update state if component is still mounted
        if (!abortController.signal.aborted && isMounted) {
          permissionsLoadedRef.current = true;
          setPermissionsLoaded(true); // Mark as loaded even on error
        }
      }
    };

    loadPermissions();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [user?.id, adminClient, isAuthenticated, userProfile]);

  // Reset permissions on logout
  React.useEffect(() => {
    if (!isAuthenticated) {
      permissionsLoadedRef.current = false;
      setPermissionsLoaded(false);
      sessionStorage.removeItem("_perm_check");
    }
  }, [isAuthenticated]);

  // Mobile viewport forces compact; desktop respects the persisted user preference.
  useEffect(() => {
    if (isMobile) setIsSidebarCompact(true);
  }, [isMobile, setIsSidebarCompact]);

  // The react-ui BrandProvider owns the `data-brand` attribute (set from the
  // active pack key inside applyBrand), so pathway no longer manages it here.

  // Effect hook to handle the async operation
  useEffect(() => {
    const fetchUserData = async () => {
      if (
        user &&
        user.email &&
        adminClient &&
        user.id &&
        user.name &&
        !userProfile // Only fetch if we don't already have a userProfile
      ) {
        try {
          // Removed token expiration check (user.exp) as UserProfile does not have 'exp'
          const tenant = user.email.split("@")[1];

          const userProfile: UserInput = {
            id: String(user.id),
            tenant: tenant,
            email: user.email,
            name: String(user.name),
            picture: String(user.picture || ""),
          };
          const userData = await adminClient.users.create(userProfile);

          if (!userData.id || !userData.tenant) {
            throw new Error("Received user data is missing required id or tenant");
          }

          // Resolve the visual brand from the user's email domain. Some tenants
          // are identified by run mode rather than email: NGV and NGET both use
          // @nationalgrid.com addresses, and LSEG's risk environment is used by
          // developers whose email domain does not resolve to the LSEG brand.
          // Never override an already-selected mode-identified brand. Compare
          // against the resolved tenant key so the guard survives any
          // display-name change.
          const MODE_IDENTIFIED_KEYS: readonly TenantKey[] = ["ngv", "nget", "lseg"];
          const emailTenantKey = tenantKeyForEmailDomain(tenant);
          const keepModeIdentifiedBrand = MODE_IDENTIFIED_KEYS.includes(tenantKey);

          if (!keepModeIdentifiedBrand) {
            setTenantKey(emailTenantKey);
          }

          // Warn only when we genuinely fell back to the WeBuild default for an
          // unrecognised domain. @nationalgrid.com is deliberately not a "known"
          // domain (NG identity comes from run mode, not email), so an NG login
          // preserves its brand above and must not log a misleading fallback.
          if (!keepModeIdentifiedBrand && !isKnownTenantDomain(tenant)) {
            // Strip control characters before logging so an attacker-controlled
            // email domain cannot inject fake log lines.
            const sanitisedDomain = tenant.replace(/[\r\n]/g, "");

            logger.warn("Unrecognised tenant domain; falling back to WeBuild brand", {
              domain: sanitisedDomain,
            });
          }
          setUserProfile({
            ...userProfile,
            ...userData,
            id: userData.id ?? userProfile.id,
            name: String(userData.name ?? userProfile.name),
            picture: String(userData.picture ?? userProfile.picture),
          } as any);

          return userData;
        } catch (err) {
          logger.error("🚨 Error fetching user data", err instanceof Error ? err : undefined, {
            errorMessage: (err as any)?.message,
            responseStatus: (err as any)?.response?.status,
            adminClientURL: String(adminClient?._baseURL ?? ""),
          });
          // Only logout if this is not a validation error - let user stay logged in for now.
          // In embedded (iframe) mode never logout here: that would post REQUEST_LOGOUT and
          // end the host session over a possibly transient admin API error.
          if (!isEmbedded && !(err as any)?.message?.includes("Response validation failed")) {
            logout("error");
          }
        }
      }
    };

    if (
      !isLoading &&
      isAuthenticated &&
      user &&
      user.email &&
      user.id &&
      user.name &&
      !userProfile
    ) {
      fetchUserData();
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    adminClient,
    setTenantKey,
    tenantKey,
    setUserProfile,
    userProfile,
    logout,
    isEmbedded,
  ]);

  // Effect to handle user profile updates via API
  useEffect(() => {
    const updateUserProfileAPI = async () => {
      if (adminClient && userProfile && previousUserProfileRef.current) {
        logger.info("update user profile");
        // Check if userProfile has changed (excluding system fields)
        const {
          id: _cId,
          name: _cName,
          tenant: _cTenant,
          email: _cEmail,
          picture: _cPicture,
          ...currentProfile
        } = userProfile;
        const {
          id: _pId,
          name: _pName,
          tenant: _pTenant,
          email: _pEmail,
          picture: _pPicture,
          ...previousProfile
        } = previousUserProfileRef.current;

        // Check if any user-modifiable fields have changed
        const hasChanges = JSON.stringify(currentProfile) !== JSON.stringify(previousProfile);

        logger.info("hasChanges", { hasChanges: String(hasChanges) });
        if (hasChanges) {
          try {
            await adminClient.users.updateUser({
              userId: userProfile.id,
              userInput: userProfile,
            });
          } catch (error) {
            logger.error(
              "Failed to update user profile",
              error instanceof Error ? error : undefined,
            );
            // Surface the failure to the user. Callers of
            // `useUser().updateUserProfile` (e.g. PromptShortcutsSettings.
            // handleSaveShortcut) cannot catch this from their own try/catch:
            // the React-auth hook's `updateUserProfile` is a synchronous
            // Context setter and the actual PUT happens here. Without this
            // toast, save failures stay silent and the optimistic UI suggests
            // the change was persisted when it was not.
            toast.danger(t("Failed to save changes. Please try again."));
          }
        }
      }
      previousUserProfileRef.current = userProfile;
    };

    updateUserProfileAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, adminClient]);

  // Show loading page until:
  // 1. Auth is complete (not isLoading)
  // 2. User profile is loaded
  // 3. Permissions are loaded
  if (isLoading || (isAuthenticated && (!userProfile || !permissionsLoaded))) {
    return <LoadingPage />;
  }

  // While the embedded (iframe) handshake is still resolving (ssoSilent in flight or
  // awaiting the parent's token), show the loading screen rather than flashing LoginPrompt.
  if (isEmbedded && !isAuthenticated && embeddedStatus === "resolving") {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <LoginPrompt />;
  }

  return (
    <DefaultLayout>
      {/* Only run tamper detection after permissions are loaded */}
      {permissionsLoaded && <TamperDetection />}
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingContent />}>
          <Outlet />
        </React.Suspense>
      </ChunkErrorBoundary>
    </DefaultLayout>
  );
};

export default function App() {
  const { brand } = useBrand();

  useEffect(() => {
    // Log version information on app load
    logger.info(`📦 Pathway v${VERSION_INFO.version}`, {
      commit: VERSION_INFO.commit,
      branch: VERSION_INFO.branch,
      buildDate: VERSION_INFO.buildDate,
    });

    function setAppHeight() {
      document.documentElement.style.setProperty("--app-height", `${window.innerHeight}px`);
    }
    window.addEventListener("resize", setAppHeight);

    setAppHeight();

    return () => window.removeEventListener("resize", setAppHeight);
  }, []);

  // The browser-tab favicon follows the active tenant automatically: react-ui's
  // BrandProvider applies brand.logoUrls.favicon to the document <link rel="icon">
  // via applyBrand (setFavicon defaults on). index.html ships the static default
  // shown before the brand resolves.

  return (
    <Routes>
      {/* OAuth callback outside PrivateRoute - popup doesn't need full auth context */}
      <Route element={<IntegrationCallback />} path="/settings/integrations/callback" />
      <Route element={<PrivateRoute />} path="/">
        <Route index element={<IndexPage />} />
        <Route path="/chat">
          <Route index element={<WorkflowPage />} />
          <Route element={<WorkflowPage />} path=":chatId" />
        </Route>
        <Route element={<AccessDeniedPage />} path="/access-denied" />
        <Route path="/workflows">
          {/* The standalone Workflows index was folded into Library (ADR-0019).
              The route is kept as a redirect rather than removed so existing
              bookmarks, deep links and the IframeDashboard NAVIGATE allowlist
              still resolve. Child routes below are unaffected. */}
          <Route index element={<Navigate replace to="/library?tab=workflow" />} />
          <Route path="playground">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPlaygroundPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPlaygroundPage />
                </ProtectedRoute>
              }
              path=":workflowId"
            />
          </Route>
          <Route path=":workflowId">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowPage />
                </ProtectedRoute>
              }
              path=":chatId"
            />
            <Route element={<WorkflowEditRedirect />} path="edit" />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowDatasetsPage />
                </ProtectedRoute>
              }
              path="datasets"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <WorkflowMetricsPage />
                </ProtectedRoute>
              }
              path="metrics"
            />
            <Route
              element={
                <ProtectedRoute requiredPermission="workflows">
                  <RunPage />
                </ProtectedRoute>
              }
              path=":runId"
            />
          </Route>
        </Route>
        <Route path="/metrics">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="monitoring">
                <MetricsPage />
              </ProtectedRoute>
            }
          />
        </Route>
        {isFolder(brand, "webuild") && (
          <Route path="/compliance">
            <Route
              index
              element={
                <ProtectedRoute requiredPermission="monitoring">
                  <ComplianceDashboardPage />
                </ProtectedRoute>
              }
            />
          </Route>
        )}
        <Route path="/settings">
          <Route index element={<SettingsPage />} />
        </Route>
        <Route element={<Navigate replace to="/settings#admin" />} path="/admin" />
        <Route path="/datahub">
          <Route
            index
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubIndexPage />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubFilesPage />
              </ProtectedRoute>
            }
            path="files"
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubMCPServerPage />
              </ProtectedRoute>
            }
            path="mcp"
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubGoogleDrive />
              </ProtectedRoute>
            }
            path="google-drive"
          />
          <Route
            element={
              <ProtectedRoute requiredPermission="knowledge_hub">
                <DataHubConfluence />
              </ProtectedRoute>
            }
            path="confluence"
          />
        </Route>
        {/* Prompt Library was folded into Library (ADR-0019). Kept as a
            redirect for the same reasons as /workflows above. */}
        <Route element={<Navigate replace to="/library?tab=prompt" />} path="prompts" />
        {/* Models Overview - always available (no permission required) */}
        <Route element={<ModelsPage />} path="/models" />
        {/* Library - intentionally unprotected, like /models: skills and
            connectors are visible to every user via the chat composer, so the
            page applies per-source permission gates internally instead. */}
        <Route element={<LibraryPage />} path="/library" />
        {/* Dashboard routes. "dashboard" is the default segment for tenants whose
            dashboard has no custom urlSegment (WeBuild lands here via IndexPage's
            redirect); custom segments (NGV's /ncrm) are declared per tenant in
            src/config/dashboards.ts. DashboardPage redirects to "/" when the active
            tenant has no dashboard, so the default route is inert for those. */}
        {[...new Set(["dashboard", ...dashboardUrlSegments(tenantFolders)])].map((segment) => (
          <Route key={segment} element={<DashboardPage />} path={`/${segment}/*`} />
        ))}
        {/* Workflow Builder - available to every tenant, reusing the Playground
            component set (LSEG-91). No isFolder gating: styling differences are
            expressed through brand tokens only. The `canAuthorWorkflows`
            capability is required on top of the `workflows` flag, because that
            flag is computed from the RBAC read grant and the Builder saves. */}
        <Route path="/workflow-builder">
          <Route
            index
            element={
              <ProtectedRoute
                requiredCapability="canAuthorWorkflows"
                requiredPermission="workflows"
              >
                <WorkflowBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute
                requiredCapability="canAuthorWorkflows"
                requiredPermission="workflows"
              >
                <WorkflowBuilderPage />
              </ProtectedRoute>
            }
            path=":workflowId"
          />
        </Route>
        {/* Automated Workflows (LSEG-107) - scheduled executions of published
            workflows plus run history. Takes the same gate and the same
            no-isFolder-gating position as Workflow Builder above: scheduling a
            workflow and triggering a run are mutations, so the `workflows` flag
            computed from the RBAC read grant is not sufficient on its own. */}
        <Route
          element={
            <ProtectedRoute requiredCapability="canAuthorWorkflows" requiredPermission="workflows">
              <AutomationsPage />
            </ProtectedRoute>
          }
          path="/automations"
        />
        {/* Catch-all route for 404 */}
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Routes>
  );
}
